import { useEffect, useRef } from 'react'
import { SendHorizontal } from 'lucide-react'
import { TemplatePicker } from './TemplatePicker'
import { insertTemplateBody, unfilledPlaceholders } from '@/lib/format'

interface ReplyBarProps {
  value: string
  onChange: (v: string) => void
  onSubmit: () => void
  placeholder?: string
  disabled?: boolean
  /** Always-visible line above the input, for when "send" means more than a reply. */
  notice?: string
}

// Tallest the input grows before it starts scrolling (~5 lines).
const MAX_HEIGHT = 120

// Bottom reply input shared by broadcast threads and group chats. Enter submits,
// Shift+Enter adds a line. It's a textarea rather than an <input> because
// templates are often multi-line and a text input silently strips newlines.
export function ReplyBar({ value, onChange, onSubmit, placeholder, disabled, notice }: ReplyBarProps) {
  const unfilled = unfilledPlaceholders(value)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Grow with the content (a single line looks identical to the old pill input).
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`
  }, [value])

  return (
    <div className="flex-none border-t border-border bg-surface px-4 py-3">
      {notice && (
        <div className="mb-2 text-[12px] font-semibold text-warn-ink">{notice}</div>
      )}
      {unfilled.length > 0 && (
        <div className="mb-2 text-[12px] font-semibold text-danger-ink">
          Fill in {unfilled.join(', ')} before sending
        </div>
      )}
      <div className="flex items-end gap-2.5">
      <TemplatePicker
        variant="icon"
        onSelect={(t) => onChange(insertTemplateBody(value, t.body))}
      />
      <textarea
        ref={inputRef}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (unfilled.length === 0) onSubmit()
          }
        }}
        placeholder={placeholder}
        className="max-h-[120px] flex-1 resize-none rounded-[23px] border-[1.5px] border-border bg-surface-2 px-[17px] py-3 text-sm leading-5 text-foreground outline-none"
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled || !value.trim() || unfilled.length > 0}
        aria-label="Send"
        className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-primary text-on-primary transition-opacity disabled:opacity-50"
      >
        <SendHorizontal className="h-[18px] w-[18px]" />
      </button>
      </div>
    </div>
  )
}
